import parseLocale from '@/core/i18n/parse-locale';
import composeLocale from '@/core/utils/locale/compose-locale';

export type NonEcommerceMarketLocale = {
  languageId: string;
  languageName: string;
  locale: string;
};

export type NonEcommerceMarket = {
  countryId: string;
  countryName: string;
  locales: NonEcommerceMarketLocale[];
  /** Uniform Canvas composition slug for `/`. Defaults to {@link DEFAULT_BROWSE_ONLY_UNIFORM_ROOT_SLUG}. */
  uniformRootSlug?: string;
};

/** Uniform Canvas composition slug for generic content-only experiences. */
export const DEFAULT_BROWSE_ONLY_UNIFORM_ROOT_SLUG = 'non-eva-root';

/**
 * Explicitly configured content-only markets (no EVA webshop).
 * Expansion markets (UK, CH) are handled separately via feature flag in `expansion-markets.ts`.
 */
export const CONFIGURED_NON_ECOMMERCE_MARKETS: NonEcommerceMarket[] = [
  {
    countryId: 'AE',
    countryName: 'United Arab Emirates',
    locales: [
      {
        languageId: 'en',
        languageName: 'English',
        locale: 'en-ae',
      },
    ],
  },
];

export const CONFIGURED_NON_ECOMMERCE_LOCALES =
  CONFIGURED_NON_ECOMMERCE_MARKETS.flatMap(market =>
    market.locales.map(locale => locale.locale.toLowerCase()),
  );

export function getNonEcommerceMarketByCountry(
  countryCode: string,
): NonEcommerceMarket | undefined {
  const normalized = countryCode.toUpperCase();
  return CONFIGURED_NON_ECOMMERCE_MARKETS.find(
    market => market.countryId === normalized,
  );
}

export function getNonEcommerceMarketByLocale(
  locale: string,
): NonEcommerceMarket | undefined {
  const normalized = locale.toLowerCase();
  return CONFIGURED_NON_ECOMMERCE_MARKETS.find(market =>
    market.locales.some(entry => entry.locale.toLowerCase() === normalized),
  );
}

export function getNonEcommerceLocalesByCountry(
  countryCode: string,
): NonEcommerceMarketLocale[] {
  return getNonEcommerceMarketByCountry(countryCode)?.locales ?? [];
}

export function getBrowseOnlyUniformRootSlug(locale: string): string {
  return (
    getNonEcommerceMarketByLocale(locale)?.uniformRootSlug ??
    DEFAULT_BROWSE_ONLY_UNIFORM_ROOT_SLUG
  );
}

export function pickLocaleForCountry(
  locales: NonEcommerceMarketLocale[],
  browserLang: string | undefined,
  fallbackLocale: string,
): string {
  if (browserLang) {
    const match = locales.find(
      entry => entry.languageId.toLowerCase() === browserLang.toLowerCase(),
    );
    if (match) {
      return match.locale.toLowerCase();
    }
  }
  return (locales.at(0)?.locale ?? fallbackLocale).toLowerCase();
}

export function resolveConfiguredNonEcommerceLocale(
  countryCode: string,
  browserLang: string | undefined,
  fallbackLocale: string,
): string | undefined {
  const market = getNonEcommerceMarketByCountry(countryCode);
  if (!market) {
    return undefined;
  }
  return pickLocaleForCountry(market.locales, browserLang, fallbackLocale);
}

/** True when the locale belongs to an explicitly configured non-ecommerce market. */
export function isConfiguredNonEcommerceLocale(locale: string): boolean {
  return CONFIGURED_NON_ECOMMERCE_LOCALES.includes(locale.toLowerCase());
}

/** True when the country is explicitly configured as a non-ecommerce market. */
export function isConfiguredNonEcommerceCountry(countryCode: string): boolean {
  return CONFIGURED_NON_ECOMMERCE_MARKETS.some(
    market => market.countryId === countryCode.toUpperCase(),
  );
}

/**
 * Generic content-only locale for geo-detected countries without EVA and without explicit config.
 * Uses supported browser language + detected country (e.g. `en-jp`).
 */
export function buildGenericBrowseOnlyLocale(
  countryCode: string,
  browserLang: string | undefined,
  fallbackLanguage: string,
): string {
  const language = browserLang ?? fallbackLanguage;
  return composeLocale(language, countryCode) ?? fallbackLanguage.toLowerCase();
}

export function getCountryIdFromLocale(locale: string): string {
  return parseLocale(locale).country.toUpperCase();
}
